// Gap store — gaps and open questions are not a separate collection; they
// are cards with `kind === 'gap'` / `'question'` that live in the card
// store. This module is the derived view over those, plus the two user
// verdicts that resolve a gap: Approve (promote to the proposed kind) and
// Reject. Discuss and Distill are cross-cutting flows (they spawn a thread
// and a turn) and live one layer up, in CoreProvider.

import { useCallback, useMemo, useRef } from 'react'
import { api } from '../api'
import type { Card } from './types'

export interface GapStore {
  /** Agent proposals awaiting a verdict (`kind === 'gap'`). */
  gaps: Card[]
  /** Open questions the user can answer (`kind === 'question'`). */
  questions: Card[]
  /** Approve a gap → promote it to its proposed kind. An empty input lets
   *  the server fall back to the proposal's stored title / body. */
  approveGap: (
    cardId: string,
    input?: { to_kind?: string; title?: string; body?: string },
  ) => Promise<void>
  /** Reject a gap → delete it and record `dismissed_gaps` on its blocks. */
  rejectGap: (cardId: string, reason?: string) => Promise<void>
}

export function useGapStore(
  cards: Card[],
  activeProjectId: string | null,
): GapStore {
  // Ref so the verdict callbacks stay referentially stable across project
  // switches (rare; not worth re-creating handlers for).
  const projectRef = useRef(activeProjectId)
  projectRef.current = activeProjectId

  const gaps = useMemo(() => cards.filter((c) => c.kind === 'gap'), [cards])
  const questions = useMemo(
    () => cards.filter((c) => c.kind === 'question'),
    [cards],
  )

  // Verdicts mutate server-side; the resulting card_written / card_deleted
  // SSE events drive the card store's refetch, so there is no local
  // mutation to do here.
  const approveGap = useCallback(
    async (
      cardId: string,
      input?: { to_kind?: string; title?: string; body?: string },
    ) => {
      const pid = projectRef.current
      if (!pid) return
      await api.promoteCard(pid, cardId, input ?? {})
    },
    [],
  )

  const rejectGap = useCallback(async (cardId: string, reason?: string) => {
    const pid = projectRef.current
    if (!pid) return
    await api.rejectGap(pid, cardId, reason ? { reason } : {})
  }, [])

  return { gaps, questions, approveGap, rejectGap }
}
